import { ReactNode } from 'react';
import { SidebarPage } from '@backstage/core-components';
import {
  coreExtensionData,
  createExtension,
  createExtensionInput,
} from '@backstage/frontend-plugin-api';
import { SearchModalProvider } from '@backstage/plugin-search';
import { makeStyles } from '@material-ui/core';
import { TOP_BAR_HEIGHT, TopBar } from './TopBar';

const GAP = 8;

const useStyles = makeStyles(theme => ({
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: theme.palette.navigation.background,
    '@media print': { display: 'none' },
  },
  // The rounded panel the page renders into. SidebarPage already pads the
  // left edge by the sidebar width, so only top/right/bottom are set here.
  panel: {
    position: 'relative',
    margin: `${TOP_BAR_HEIGHT}px ${GAP}px ${GAP}px 0`,
    height: `calc(100vh - ${TOP_BAR_HEIGHT + GAP}px)`,
    borderRadius: 16,
    overflow: 'auto',
    background: theme.palette.background.default,
    boxShadow: '0 1px 2px rgba(0,0,0,0.24), 0 16px 40px -12px rgba(0,20,60,0.65)',
    '@media print': { margin: 0, height: 'auto', borderRadius: 0, boxShadow: 'none' },
    [theme.breakpoints.down('xs')]: {
      margin: `${TOP_BAR_HEIGHT}px 0 0 0`,
      borderRadius: 0,
      boxShadow: 'none',
    },
  },
  // The new frontend system renders a per-page breadcrumb strip (@backstage/ui's
  // PluginHeader) above every page's content. Restyle it here, globally, as a
  // quiet trail: it clears the panel's rounded corner, lines up with the 24px
  // content edge, drops the hairline rule, mutes parent crumbs and makes the
  // current page the only emphasised item. Class names are content-hashed CSS
  // modules, so match on stable substrings. The custom properties need
  // `!important` because PluginHeader redeclares them in a higher-specificity
  // `:has(+ ...)` variant when a page has its own banner right after it.
  '@global': {
    '[class~="bui-PluginHeader"]': {
      '--bui-plugin-header-padding-top': '14px !important',
      '--bui-plugin-header-toolbar-padding-bottom': '4px !important',
      '--bui-plugin-header-toolbar-border-bottom': 'none !important',
      '--bui-plugin-header-margin-bottom': '8px !important',
      '--bui-plugin-header-breadcrumbs-gap': '8px !important',
      '--bui-plugin-header-breadcrumbs-max-width': '220px !important',
      paddingInline: '24px !important',
    },
    // Pages that bring their own header sit flush under the strip.
    '[class~="bui-PluginHeader"]:has(+ [data-backstage-core-header]), [class~="bui-PluginHeader"]:has(+ [data-backstage-core-page] [data-backstage-core-header])': {
      '--bui-plugin-header-margin-bottom': '0px !important',
    },
    // The home page's trail is a lone "Home" crumb that links to itself, so the
    // whole strip is dropped there.
    '[class~="bui-PluginHeader"]:has([class*="PluginHeaderBreadcrumbs"] li:only-child > a[href="/"])': {
      display: 'none !important',
    },
    '[class*="PluginHeaderToolbarContent"]': {
      padding: '0 !important',
      gap: '10px !important',
    },
    '[class*="PluginHeaderToolbarIcon"]': {
      width: '24px !important',
      height: '24px !important',
      borderRadius: '8px !important',
      color: '#0058AB !important',
      background: 'rgba(0,88,171,0.09) !important',
    },
    '[class*="PluginHeaderToolbarIcon"] svg': {
      width: '14px !important',
      height: '14px !important',
    },
    '[class*="PluginHeaderBreadcrumbs"] li > *:not(span[aria-hidden])::first-letter': {
      textTransform: 'uppercase',
    },
    '[class*="PluginHeaderBreadcrumbs"] a': {
      fontSize: '13px !important',
      fontWeight: '500 !important',
      color: '#5B6485 !important',
      textDecoration: 'none !important',
    },
    '[class*="PluginHeaderBreadcrumbs"] a:hover': {
      color: '#0058AB !important',
    },
    '[class*="PluginHeaderBreadcrumbs"] li[data-current] > *': {
      fontSize: '13px !important',
      fontWeight: '600 !important',
      color: '#121A38 !important',
    },
    '[class*="PluginHeaderBreadcrumbs"] li > span[aria-hidden]': {
      color: '#9AA1BC !important',
    },
    '[class*="PluginHeaderBreadcrumbs"] li > span[aria-hidden] svg': {
      width: '14px !important',
      height: '14px !important',
    },
  },
}));

const Shell = ({ nav, content }: { nav: ReactNode; content: ReactNode }) => {
  const classes = useStyles();
  return (
    <SearchModalProvider>
      <div className={classes.backdrop} />
      <SidebarPage>
        <TopBar />
        {nav}
        <main className={classes.panel}>{content}</main>
      </SidebarPage>
    </SearchModalProvider>
  );
};

// Replaces plugin-app's `app/layout` (same extension id) so pages render into
// the rounded panel without any page-level changes.
export const SwoopLayout = createExtension({
  name: 'layout',
  attachTo: { id: 'app/root', input: 'children' },
  inputs: {
    nav: createExtensionInput([coreExtensionData.reactElement], {
      singleton: true,
    }),
    content: createExtensionInput([coreExtensionData.reactElement], {
      singleton: true,
    }),
  },
  output: [coreExtensionData.reactElement],
  factory: ({ inputs }) => [
    coreExtensionData.reactElement(
      <Shell
        nav={inputs.nav.get(coreExtensionData.reactElement)}
        content={inputs.content.get(coreExtensionData.reactElement)}
      />,
    ),
  ],
});
